import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';

const DEFAULT_FOOTER = {
  brandDescription: 'A modern co-working space in the heart of Crookwell. Flexible desks, private offices, and meeting rooms for professionals, freelancers, and remote workers.',
  address: 'Crookwell NSW 2583, Australia',
  email: 'hello@thehubcrookwell.com.au',
  phone: '',
  hoursWeekday: 'Mon–Fri 8am–6pm',
  hoursSaturday: 'Sat 9am–1pm',
  hoursMembersNote: 'Members: 24/7 access',
  tagline: 'Built for co-workers, by co-workers.',
};

export default function Footer() {
  const [config, setConfig] = useState(DEFAULT_FOOTER);

  useEffect(() => {
    getDoc(doc(db, 'config', 'footer'))
      .then((snap) => {
        if (snap.exists()) setConfig({ ...DEFAULT_FOOTER, ...snap.data() });
      })
      .catch(() => {});
  }, []);

  return (
    <footer className="bg-slate-900 text-gray-300 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-brand-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">H</span>
              </div>
              <span className="text-white font-bold text-lg">The Hub Crookwell</span>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed">
              {config.brandDescription}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
              <li><Link to="/spaces" className="hover:text-white transition-colors">Our Spaces</Link></li>
              <li><Link to="/availability" className="hover:text-white transition-colors">Availability</Link></li>
              <li><Link to="/book" className="hover:text-white transition-colors">Book a Space</Link></li>
              <li><Link to="/login" className="hover:text-white transition-colors">Member Login</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact & Hours</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li className="flex gap-2">
                <svg className="w-4 h-4 mt-0.5 text-brand-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {config.address}
              </li>
              <li className="flex gap-2">
                <svg className="w-4 h-4 mt-0.5 text-brand-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href={`mailto:${config.email}`} className="hover:text-white transition-colors">{config.email}</a>
              </li>
              {config.phone && (
                <li className="flex gap-2">
                  <svg className="w-4 h-4 mt-0.5 text-brand-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <a href={`tel:${config.phone.replace(/\s/g, '')}`} className="hover:text-white transition-colors">{config.phone}</a>
                </li>
              )}
              <li className="flex gap-2">
                <svg className="w-4 h-4 mt-0.5 text-brand-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>
                  {config.hoursWeekday}<br />
                  {config.hoursSaturday}<br />
                  <span className="text-brand-400">{config.hoursMembersNote}</span>
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-slate-700 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-500">
          <p>© {new Date().getFullYear()} The Hub Crookwell. All rights reserved.</p>
          <p>{config.tagline}</p>
        </div>
      </div>
    </footer>
  );
}
